import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  CalendarClock,
  CheckCircle2,
  Edit2,
  LayoutGrid,
  Loader2,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
  UserPlus,
  Warehouse,
} from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import { SelectMenu } from '../../ui/SelectMenu';

type Staff = {
  id: string;
  role: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  active: boolean;
  notes?: string;
  userId?: string;
};

type UserLinkOption = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  linkedProfileType?: 'PROVIDER' | 'STAFF' | null;
  linkedProviderProfileId?: string | null;
  linkedStaffProfileId?: string | null;
};

type Resource = {
  id: string;
  name: string;
  type: string;
  locationId?: string;
  description?: string;
  active: boolean;
  reservations?: ResourceReservation[];
};

type ResourceReservation = {
  id: string;
  resourceId: string;
  title: string;
  start: string;
  end: string;
  status: string;
};

type Section = 'staff' | 'assets';

const STAFF_ROLES = [
  'Secretary',
  'Front Desk',
  'Assistant',
  'Receptionist',
  'Office Admin',
  'Billing Clerk',
  'Insurance Coordinator',
  'Manager / Owner',
];
const showPrefill = import.meta.env.DEV;
const RESOURCE_TYPE_PRESETS = ['Room', 'Treatment Room', 'Equipment', 'Device', 'Laser', 'Chair'];

type ResourceTypeFilter = 'all' | 'rooms' | 'equipment' | 'inactive';
type ResourceSortMode = 'manual' | 'name' | 'type' | 'location';

function isRoomResourceType(type: string) {
  return /room|suite|bay|bed|ward|or|theatre|theater|icu/i.test(type);
}

function loadResourceOrder() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem('settings.resourceOrder') || '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export default function SettingsResources() {
  const navigate = useNavigate();
  const { tr } = useI18n();

  const cards = [
    {
      title: tr('Practice users'),
      description: tr('Manage non-clinical users tied to front desk, billing, and office operations.'),
      icon: UserPlus,
      path: '/settings/practice-users',
      color: 'bg-cyan-50 text-cyan-700',
    },
    {
      title: tr('Schedule assets'),
      description: tr('Manage rooms, equipment, and other schedulable assets used by booking workflows.'),
      icon: Warehouse,
      path: '/settings/schedule-assets',
      color: 'bg-indigo-50 text-indigo-700',
    },
    {
      title: tr('Room management'),
      description: tr('Upload floor plans, map shared rooms, and control the visual room board layout.'),
      icon: LayoutGrid,
      path: '/settings/room-management',
      color: 'bg-emerald-50 text-emerald-700',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-ink">{tr('Resources & Staff')}</h1>
          <p className="text-slate-500 text-sm">{tr('Choose which operational settings area you want to manage.')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <button
            key={card.path}
            onClick={() => navigate(card.path)}
            className="card flex min-h-[200px] flex-col items-start rounded-2xl border border-slate-200 p-6 text-left transition hover:border-slate-300 hover:shadow-md"
          >
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${card.color}`}>
              <card.icon size={22} />
            </div>
            <h2 className="text-lg font-semibold text-ink">{card.title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">{card.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SettingsPracticeUsers() {
  return <SettingsResourcesSection section="staff" />;
}

export function SettingsScheduleAssets() {
  return <SettingsResourcesSection section="assets" />;
}

function SettingsResourcesSection({ section }: { section: Section }) {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr, t } = useI18n();

  const [staff, setStaff] = useState<Staff[]>([]);
  const [userOptions, setUserOptions] = useState<UserLinkOption[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [searchStaff, setSearchStaff] = useState('');
  const [searchResource, setSearchResource] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<ResourceTypeFilter>('all');
  const [resourceSortMode, setResourceSortMode] = useState<ResourceSortMode>('manual');
  const [resourceOrder, setResourceOrder] = useState<string[]>(() => loadResourceOrder());
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState<Staff>({
    id: '',
    role: STAFF_ROLES[0],
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    active: true,
    notes: '',
    userId: '',
  });
  const [staffErrors, setStaffErrors] = useState<Record<string, string>>({});

  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceForm, setResourceForm] = useState<Resource>({
    id: '',
    name: '',
    type: '',
    locationId: '',
    description: '',
    active: true,
    reservations: [],
  });
  const [resourceErrors, setResourceErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setFailure(null);
      try {
        if (section === 'staff') {
          const [staffData, userData] = await Promise.all([
            fetchJson<Staff[]>('/staff'),
            fetchJson<UserLinkOption[]>('/users/link-options?kind=staff'),
          ]);
          setStaff(staffData);
          setUserOptions(Array.isArray(userData) ? userData : []);
        } else {
          const resourceData = await fetchJson<Resource[]>('/resources');
          setResources(resourceData);
        }
      } catch (err: any) {
        setFailure(err.message ?? tr('Failed to load data'));
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchJson, section, tr]);

  useEffect(() => {
    try {
      window.localStorage.setItem('settings.resourceOrder', JSON.stringify(resourceOrder));
    } catch {
      // Local ordering is a convenience only; failure should not block resource management.
    }
  }, [resourceOrder]);

  const filteredStaff = useMemo(() => {
    const q = searchStaff.toLowerCase();
    return staff.filter(
      (s) =>
        s.firstName.toLowerCase().includes(q) ||
        s.lastName.toLowerCase().includes(q) ||
        s.role.toLowerCase().includes(q),
    );
  }, [staff, searchStaff]);

  const filteredResources = useMemo(() => {
    const q = searchResource.toLowerCase();
    const manualRank = new Map(resourceOrder.map((id, index) => [id, index]));
    const next = resources.filter((r) => {
      const matchesQuery =
        r.name.toLowerCase().includes(q) ||
        r.type.toLowerCase().includes(q) ||
        (r.locationId || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q);
      if (!matchesQuery) return false;
      if (resourceTypeFilter === 'inactive') return !r.active;
      if (!r.active) return false;
      if (resourceTypeFilter === 'rooms') return isRoomResourceType(r.type);
      if (resourceTypeFilter === 'equipment') return !isRoomResourceType(r.type);
      return true;
    });

    return [...next].sort((a, b) => {
      if (resourceSortMode === 'name') return a.name.localeCompare(b.name);
      if (resourceSortMode === 'type') return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      if (resourceSortMode === 'location') return (a.locationId || '').localeCompare(b.locationId || '') || a.name.localeCompare(b.name);
      return (manualRank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (manualRank.get(b.id) ?? Number.MAX_SAFE_INTEGER);
    });
  }, [resources, resourceOrder, resourceSortMode, resourceTypeFilter, searchResource]);

  const openNewStaff = () => {
    setStaffForm({
      id: '',
      role: STAFF_ROLES[0],
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      active: true,
      notes: '',
      userId: '',
    });
    setStaffErrors({});
    setBanner(null);
    setFailure(null);
    setShowStaffModal(true);
  };

  const openEditStaff = (s: Staff) => {
    setStaffForm(s);
    setStaffErrors({});
    setBanner(null);
    setFailure(null);
    setShowStaffModal(true);
  };

  const prefillStaff = () => {
    setStaffForm({
      id: '',
      role: 'Front Desk',
      firstName: 'Jordan',
      lastName: 'Lee',
      email: 'jordan.lee@example.com',
      phone: '555-410-8899',
      active: true,
      notes: 'Covers mornings, trains new hires, handles patient inbox triage.',
      userId: '',
    });
    setStaffErrors({});
  };

  const openNewResource = () => {
    setResourceForm({
      id: '',
      name: '',
      type: '',
      locationId: '',
      description: '',
      active: true,
      reservations: [],
    });
    setResourceErrors({});
    setBanner(null);
    setFailure(null);
    setShowResourceModal(true);
  };

  const openEditResource = (r: Resource) => {
    setResourceForm(r);
    setResourceErrors({});
    setBanner(null);
    setFailure(null);
    setShowResourceModal(true);
  };

  const prefillResource = () => {
    setResourceForm({
      id: '',
      name: 'Ultrasound Room 2',
      type: 'Room',
      locationId: 'loc-001',
      description: 'GE Logiq console; hold Fridays 8-10a for maintenance.',
      active: true,
      reservations: [],
    });
    setResourceErrors({});
  };

  function validateStaff() {
    const next: Record<string, string> = {};
    if (!staffForm.firstName.trim()) next.firstName = tr('First name is required');
    if (!staffForm.lastName.trim()) next.lastName = tr('Last name is required');
    if (!staffForm.role.trim()) next.role = tr('Role is required');
    return next;
  }

  function validateResource() {
    const next: Record<string, string> = {};
    if (!resourceForm.name.trim()) next.name = tr('Name is required');
    if (!resourceForm.type.trim()) next.type = tr('Type is required');
    return next;
  }

  async function saveStaff(e: React.FormEvent) {
    e.preventDefault();
    const errs = validateStaff();
    setStaffErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = { ...staffForm };
      payload.userId = payload.userId?.trim() ? payload.userId.trim() : undefined;
      const path = staffForm.id ? `/staff/${staffForm.id}` : '/staff';
      const res = await fetchApi(path, {
        method: staffForm.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Save failed (${res.status})`);
      }
      const saved = (await res.json()) as Staff;
      setStaff((prev) => (prev.some((s) => s.id === saved.id) ? prev.map((s) => (s.id === saved.id ? saved : s)) : [saved, ...prev]));
      const refreshedUsers = await fetchJson<UserLinkOption[]>('/users/link-options?kind=staff');
      setUserOptions(Array.isArray(refreshedUsers) ? refreshedUsers : []);
      setShowStaffModal(false);
      setBanner(tr('Staff saved'));
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to save staff'));
    } finally {
      setSaving(false);
    }
  }

  async function saveResource(e: React.FormEvent) {
    e.preventDefault();
    const errs = validateResource();
    setResourceErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = { ...resourceForm };
      const path = resourceForm.id ? `/resources/${resourceForm.id}` : '/resources';
      const res = await fetchApi(path, {
        method: resourceForm.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const saved = (await res.json()) as Resource;
      setResources((prev) => (prev.some((r) => r.id === saved.id) ? prev.map((r) => (r.id === saved.id ? saved : r)) : [saved, ...prev]));
      setShowResourceModal(false);
      setBanner(tr('Resource saved'));
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to save resource'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteStaff(id: string) {
    if (!window.confirm(tr('Delete this staff profile?'))) return;
    try {
      const res = await fetchApi(`/staff/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setStaff((prev) => prev.filter((s) => s.id !== id));
      const refreshedUsers = await fetchJson<UserLinkOption[]>('/users/link-options?kind=staff');
      setUserOptions(Array.isArray(refreshedUsers) ? refreshedUsers : []);
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to delete staff'));
    }
  }

  async function deleteResource(id: string) {
    if (!window.confirm(tr('Delete this resource?'))) return;
    try {
      const res = await fetchApi(`/resources/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setResources((prev) => prev.filter((r) => r.id !== id));
      setResourceOrder((prev) => prev.filter((resourceId) => resourceId !== id));
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to delete resource'));
    }
  }

  function moveResource(resourceId: string, direction: -1 | 1) {
    const visibleIds = filteredResources.map((resource) => resource.id);
    const visibleIndex = visibleIds.indexOf(resourceId);
    const targetId = visibleIds[visibleIndex + direction];
    if (!targetId) return;

    setResourceOrder((prev) => {
      const currentResourceIds = resources.map((resource) => resource.id);
      const next = prev.filter((id) => currentResourceIds.includes(id));
      currentResourceIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });

      const from = next.indexOf(resourceId);
      const to = next.indexOf(targetId);
      if (from < 0 || to < 0) return next;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  const renderStatus = (active: boolean) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
      {active ? tr('Active') : tr('Inactive')}
    </span>
  );

  const userById = useMemo(() => new Map(userOptions.map((option) => [option.id, option])), [userOptions]);
  const selectableUserOptions = useMemo(
    () =>
      userOptions.filter((option) => {
        if (option.linkedProfileType === 'PROVIDER') return option.id === staffForm.userId;
        if (option.linkedProfileType === 'STAFF') {
          if (option.id === staffForm.userId) return true;
          return option.linkedStaffProfileId === staffForm.id;
        }
        return true;
      }),
    [staffForm.id, staffForm.userId, userOptions],
  );

  const sectionTitle = section === 'staff' ? tr('Practice users (non-clinical)') : tr('Schedulable assets');
  const sectionDescription =
    section === 'staff'
      ? tr('Manage front desk, billing, and other non-clinical practice staff.')
      : tr('Manage rooms, equipment, and other assets available for scheduling.');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings/resources')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{sectionTitle}</h1>
            <p className="text-slate-500 text-sm">{sectionDescription}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {section === 'staff' ? (
            <button onClick={openNewStaff} className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2">
              <Plus size={16} /> {tr('Add Staff')}
            </button>
          ) : (
            <button onClick={openNewResource} className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2">
              <Plus size={16} /> {tr('Add Resource')}
            </button>
          )}
        </div>
      </div>

      {banner && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
          <CheckCircle2 size={16} /> {banner}
        </div>
      )}
      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      {section === 'staff' ? (
        <div className="card">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div>
              <p className="text-xs uppercase text-slate-400 tracking-wide">{tr('Staff')}</p>
              <h3 className="text-lg font-semibold text-ink">{tr('Practice users (non-clinical)')}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Search size={16} className="text-slate-400" />
              <input
                className="w-56 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                placeholder={tr('Search staff...')}
                value={searchStaff}
                onChange={(e) => setSearchStaff(e.target.value)}
              />
            </div>
          </div>
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4">{tr('Name')}</th>
                <th className="p-4">{tr('Role')}</th>
                <th className="p-4">{tr('Contact')}</th>
                <th className="p-4">{tr('Status')}</th>
                <th className="p-4 text-right">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
                    </span>
                  </td>
                </tr>
              )}
              {!loading && filteredStaff.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">
                    {tr('No staff found.')}
                  </td>
                </tr>
              )}
              {!loading &&
                filteredStaff.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-ink">
                      <div>
                        <div>
                          {s.lastName}, {s.firstName}
                        </div>
                        <div className="text-xs text-slate-500">
                          {s.userId
                            ? (() => {
                                const linked = userById.get(s.userId || '');
                                if (!linked) return `${tr('Linked user')}: ${s.userId}`;
                                return `${tr('Linked user')}: ${linked.firstName} ${linked.lastName}`;
                              })()
                            : tr('No linked user')}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{s.role}</td>
                    <td className="p-4 text-slate-600">
                      <div className="flex flex-col text-xs">
                        {s.email && <span>{s.email}</span>}
                        {s.phone && <span>{s.phone}</span>}
                      </div>
                    </td>
                    <td className="p-4">{renderStatus(s.active)}</td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button className="p-1 text-slate-400 hover:text-ink" onClick={() => openEditStaff(s)} aria-label={tr('Edit staff')}>
                        <Edit2 size={16} />
                      </button>
                      <button className="p-1 text-slate-400 hover:text-rose-600" onClick={() => deleteStaff(s.id)} aria-label={tr('Delete staff')}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div>
              <p className="text-xs uppercase text-slate-400 tracking-wide">{tr('Resources')}</p>
              <h3 className="text-lg font-semibold text-ink">{tr('Schedulable assets')}</h3>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-2">
                <Search size={16} className="text-slate-400" />
                <input
                  className="w-56 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder={tr('Search resources...')}
                  value={searchResource}
                  onChange={(e) => setSearchResource(e.target.value)}
                />
              </div>
              <select
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                value={resourceTypeFilter}
                onChange={(event) => setResourceTypeFilter(event.target.value as ResourceTypeFilter)}
              >
                <option value="all">{tr('All active')}</option>
                <option value="rooms">{tr('Rooms')}</option>
                <option value="equipment">{tr('Equipment')}</option>
                <option value="inactive">{tr('Inactive')}</option>
              </select>
              <select
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                value={resourceSortMode}
                onChange={(event) => setResourceSortMode(event.target.value as ResourceSortMode)}
              >
                <option value="manual">{tr('Manual order')}</option>
                <option value="name">{tr('Name')}</option>
                <option value="type">{tr('Type')}</option>
                <option value="location">{tr('Location')}</option>
              </select>
            </div>
          </div>
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4">{tr('Name')}</th>
                <th className="p-4">{tr('Type')}</th>
                <th className="p-4">{tr('Location')}</th>
                <th className="p-4">{tr('Description')}</th>
                <th className="p-4">{tr('Status')}</th>
                <th className="p-4 text-right">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
                    </span>
                  </td>
                </tr>
              )}
              {!loading && filteredResources.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-500">
                    {tr('No resources found.')}
                  </td>
                </tr>
              )}
              {!loading &&
                filteredResources.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-ink">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
                          <Warehouse size={16} />
                        </div>
                        <div>
                          <div>{r.name}</div>
                          <div className="text-xs text-slate-500">{isRoomResourceType(r.type) ? tr('Room') : tr('Equipment')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{r.type}</td>
                    <td className="p-4 text-slate-600">{r.locationId || tr('Unassigned')}</td>
                    <td className="p-4 text-slate-600 text-sm">{r.description || tr('—')}</td>
                    <td className="p-4">{renderStatus(r.active)}</td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button
                        className="p-1 text-slate-400 hover:text-ink disabled:opacity-30"
                        onClick={() => moveResource(r.id, -1)}
                        disabled={resourceSortMode !== 'manual' || filteredResources[0]?.id === r.id}
                        aria-label={tr('Move resource up')}
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        className="p-1 text-slate-400 hover:text-ink disabled:opacity-30"
                        onClick={() => moveResource(r.id, 1)}
                        disabled={resourceSortMode !== 'manual' || filteredResources[filteredResources.length - 1]?.id === r.id}
                        aria-label={tr('Move resource down')}
                      >
                        <ArrowDown size={16} />
                      </button>
                      <button className="p-1 text-slate-400 hover:text-ink" onClick={() => openEditResource(r)} aria-label={tr('Edit resource')}>
                        <Edit2 size={16} />
                      </button>
                      <button className="p-1 text-slate-400 hover:text-rose-600" onClick={() => deleteResource(r.id)} aria-label={tr('Delete resource')}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="p-4 text-xs text-slate-500 flex items-center gap-2 border-t border-slate-100">
            <CalendarClock size={14} /> {tr('Reservation tracking API available via /resources/reservations (create/read).')}
          </div>
        </div>
      )}

      {showStaffModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-[700px] max-h-[90vh] overflow-y-auto p-6 animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase text-slate-400 tracking-wide">Staff</p>
                <h2 className="text-lg font-semibold text-ink">{staffForm.id ? 'Edit staff' : 'Add staff'}</h2>
              </div>
              <div className="flex items-center gap-2">
                {showPrefill && !staffForm.id && (
                  <button
                    type="button"
                    onClick={prefillStaff}
                    className="px-3 py-1 text-xs border border-dashed border-slate-300 rounded-lg text-slate-600 hover:text-ink hover:border-ink"
                  >
                    Prefill demo data
                  </button>
                )}
                <button onClick={() => setShowStaffModal(false)} className="text-slate-500 hover:text-ink text-sm">
                  Close
                </button>
              </div>
            </div>
            <form onSubmit={saveStaff} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Role"
                  required
                  value={staffForm.role}
                  options={STAFF_ROLES}
                  onChange={(v) => setStaffForm((prev) => ({ ...prev, role: v }))}
                  error={staffErrors.role}
                />
                <label className="text-sm space-y-1 block">
                  <span className="text-slate-600 font-medium">Linked User (optional)</span>
                  <select
                    className="w-full border rounded-lg px-3 py-2 bg-white text-sm border-slate-200 focus:ring-2 focus:ring-ink/10 outline-none"
                    value={staffForm.userId || ''}
                    onChange={(e) => {
                      const nextUserId = e.target.value;
                      const selected = selectableUserOptions.find((option) => option.id === nextUserId);
                      setStaffForm((prev) => ({
                        ...prev,
                        userId: nextUserId,
                        firstName: prev.firstName || selected?.firstName || prev.firstName,
                        lastName: prev.lastName || selected?.lastName || prev.lastName,
                        email: prev.email || selected?.email || prev.email,
                      }));
                    }}
                  >
                    <option value="">No linked user</option>
                    {selectableUserOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.lastName}, {option.firstName} ({option.role}) - {option.email}
                      </option>
                    ))}
                  </select>
                </label>
                <Input label="First Name" required value={staffForm.firstName} error={staffErrors.firstName} onChange={(v) => setStaffForm((prev) => ({ ...prev, firstName: v }))} />
                <Input label="Last Name" required value={staffForm.lastName} error={staffErrors.lastName} onChange={(v) => setStaffForm((prev) => ({ ...prev, lastName: v }))} />
                <Input label="Email" value={staffForm.email || ''} onChange={(v) => setStaffForm((prev) => ({ ...prev, email: v }))} />
                <Input label="Phone" value={staffForm.phone || ''} onChange={(v) => setStaffForm((prev) => ({ ...prev, phone: v }))} />
              </div>
              <TextArea label="Notes" value={staffForm.notes || ''} onChange={(v) => setStaffForm((prev) => ({ ...prev, notes: v }))} />
              <Toggle label="Active" value={staffForm.active} onChange={(v) => setStaffForm((prev) => ({ ...prev, active: v }))} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowStaffModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft disabled:opacity-60" disabled={saving}>
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResourceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-[700px] max-h-[90vh] overflow-y-auto p-6 animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase text-slate-400 tracking-wide">Resource</p>
                <h2 className="text-lg font-semibold text-ink">{resourceForm.id ? 'Edit resource' : 'Add resource'}</h2>
              </div>
              <div className="flex items-center gap-2">
                {showPrefill && !resourceForm.id && (
                  <button
                    type="button"
                    onClick={prefillResource}
                    className="px-3 py-1 text-xs border border-dashed border-slate-300 rounded-lg text-slate-600 hover:text-ink hover:border-ink"
                  >
                    Prefill demo data
                  </button>
                )}
                <button onClick={() => setShowResourceModal(false)} className="text-slate-500 hover:text-ink text-sm">
                  Close
                </button>
              </div>
            </div>
            <form onSubmit={saveResource} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Name" required value={resourceForm.name} error={resourceErrors.name} onChange={(v) => setResourceForm((prev) => ({ ...prev, name: v }))} />
                <div className="space-y-2">
                  <Input label="Type" required value={resourceForm.type} error={resourceErrors.type} onChange={(v) => setResourceForm((prev) => ({ ...prev, type: v }))} />
                  <div className="flex flex-wrap gap-2">
                    {RESOURCE_TYPE_PRESETS.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          resourceForm.type === type
                            ? 'border-ink bg-ink text-white'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'
                        }`}
                        onClick={() => setResourceForm((prev) => ({ ...prev, type }))}
                      >
                        {tr(type)}
                      </button>
                    ))}
                  </div>
                </div>
                <Input label="Assigned location ID" value={resourceForm.locationId || ''} onChange={(v) => setResourceForm((prev) => ({ ...prev, locationId: v }))} />
                <Input label="Description" value={resourceForm.description || ''} onChange={(v) => setResourceForm((prev) => ({ ...prev, description: v }))} />
              </div>
              <Toggle label="Active" value={resourceForm.active} onChange={(v) => setResourceForm((prev) => ({ ...prev, active: v }))} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowResourceModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft disabled:opacity-60" disabled={saving}>
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  required,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        className={`w-full border rounded-lg px-3 py-2 bg-white text-sm ${error ? 'border-rose-300' : 'border-slate-200'} focus:ring-2 focus:ring-ink/10 outline-none`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium">{label}</span>
      <textarea
        className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm resize-none h-24 focus:ring-2 focus:ring-ink/10 outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 text-sm cursor-pointer border border-slate-200 rounded-lg px-3 py-2 bg-white">
      <input type="checkbox" className="mt-1 accent-ink" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <div className="text-slate-800 font-medium">{label}</div>
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  required,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  required?: boolean;
  error?: string;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={label}
        triggerClassName={`w-full border rounded-lg px-3 py-2 bg-white text-sm ${error ? 'border-rose-300' : 'border-slate-200'}`}
        options={options.map((opt) => ({ value: opt, label: opt }))}
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </label>
  );
}
